import { useState, useEffect } from "react";
import { Card, CardContent, CardHeader, CardTitle } from "@/components/ui/card";
import { Shield, Lock, Loader2, Eye, ShieldCheck, ShieldAlert } from "lucide-react";
import { toast } from "sonner";
import { supabase } from "@/integrations/supabase/client";
import { useSimpleRole } from "@/hooks/use-simple-role";
import { cn } from "@/lib/utils";
import { privacyLevelValue, buildPrivacyUpdates } from "./privacyLevel";
import { updateDeviceField } from "@/utils/updateDeviceField";

interface ImagePrivacyCardProps {
  device: any;
  deviceId: string;
  updateField?: (field: string, value: string | number) => Promise<void>;
  /** Render without its own card frame (when placed inside another card). */
  embedded?: boolean;
}

const PRIVACY_OPTIONS = [
  {
    value: 0,
    label: "No Privacy",
    description: "Raw image — no blurring or masking.",
    Icon: Eye,
  },
  {
    value: 1,
    label: "Standard",
    description: "Faces & license plates blurred.",
    Icon: ShieldCheck,
  },
  {
    value: 2,
    label: "Maximum",
    description: "Full body & plates masked.",
    Icon: ShieldAlert,
  },
];

export const ImagePrivacyCard = ({ device, deviceId, updateField, embedded = false }: ImagePrivacyCardProps) => {
  const { role, isSystemAdmin } = useSimpleRole();
  const canEdit = role === "admin" || isSystemAdmin;

  const initial =
    device?.devicePrivacyLevel ?? device?.device_privacy_level ?? 1;
  const [value, setValue] = useState<number>(Number(initial));
  const [saving, setSaving] = useState(false);

  useEffect(() => {
    const next = device?.devicePrivacyLevel ?? device?.device_privacy_level;
    if (next !== undefined && next !== null) setValue(Number(next));
  }, [device?.devicePrivacyLevel, device?.device_privacy_level]);

  const handleChange = async (next: number) => {
    if (!canEdit || !updateField) return;
    if (next === value) return;
    const prev = value;
    setValue(next);
    setSaving(true);
    const privacyValue = privacyLevelValue(next);
    try {
      const { error } = await supabase
        .from("device_details")
        .update({ device_privacy_level: next })
        .eq("device_id", deviceId);
      if (error) throw error;

      // Text value column (see docs/notification-ai-privacy-level.sql)
      const { error: textError } = await supabase
        .from("device_details")
        .update({ notification_ai_privacy_level: privacyValue } as any)
        .eq("device_id", deviceId);
      if (textError) {
        console.error("Error saving notification_ai_privacy_level:", textError);
        toast.error("Couldn't store privacy level text in the database");
      }

      const [levelUpdate, textUpdate] = buildPrivacyUpdates(next);
      await updateField(levelUpdate[0], levelUpdate[1]);
      try {
        // Direct call so a failure is reported and rolled back
        await updateDeviceField(deviceId, textUpdate[0], textUpdate[1]);
      } catch (sendErr) {
        console.error("Error sending notificationAiPrivacyLevel:", sendErr);
        toast.error("Couldn't send privacy level to BuildShip");
        throw sendErr;
      }
    } catch (err) {
      console.error("Error updating privacy level:", err);
      toast.error("Failed to update image privacy level");
      setValue(prev);
    } finally {
      setSaving(false);
    }
  };

  const active = PRIVACY_OPTIONS.find((o) => o.value === value) ?? PRIVACY_OPTIONS[1];

  return (
    <Card className={embedded ? "border-0 shadow-none bg-transparent w-full" : "border bg-muted/50 w-full"}>
      <CardHeader className={embedded ? "py-2 px-0" : "py-2 px-4"}>
        <div className="flex items-center gap-2 flex-wrap">
          <Shield className="h-4 w-4 text-brand-500" />
          <CardTitle className="text-sm">Image Privacy Level</CardTitle>
          <span className="ml-auto flex items-center gap-2 text-[11px] text-muted-foreground">
            {saving && <Loader2 className="h-3 w-3 animate-spin" />}
            {!canEdit && (
              <span className="flex items-center gap-1">
                <Lock className="h-3 w-3" /> Admin only
              </span>
            )}
          </span>
        </div>
      </CardHeader>
      <CardContent className={embedded ? "py-2 px-0 space-y-2" : "py-2 px-4 space-y-2"}>
        <div
          role="radiogroup"
          aria-label="Image privacy level"
          className="grid grid-cols-3 gap-1.5 rounded-lg bg-background/60 border p-1"
        >
          {PRIVACY_OPTIONS.map((opt) => {
            const isActive = opt.value === value;
            const Icon = opt.Icon;
            return (
              <button
                key={opt.value}
                type="button"
                role="radio"
                aria-checked={isActive}
                disabled={!canEdit || saving}
                onClick={() => handleChange(opt.value)}
                className={cn(
                  "flex items-center gap-2 rounded-md px-2.5 py-2 transition-all border",
                  "disabled:cursor-not-allowed disabled:opacity-60",
                  isActive
                    ? "bg-[#DFEF97] text-black border-[#DFEF97] shadow-sm"
                    : "bg-transparent border-transparent hover:bg-muted"
                )}
              >
                <Icon className="h-4 w-4 shrink-0" />
                <div className="flex flex-col items-start text-left leading-none">
                  <span className="text-xs font-medium">{opt.label}</span>
                  <span className={cn("text-[10px] mt-0.5", isActive ? "text-black/60" : "text-muted-foreground")}>
                    {opt.description}
                  </span>
                </div>
              </button>
            );
          })}
        </div>
        <p className="text-[11px] text-muted-foreground leading-snug">
          {canEdit
            ? `Currently set to ${active.label}. Applies to all new images captured by this device.`
            : "Only Admin or Superuser can change this setting."}
        </p>
      </CardContent>
    </Card>
  );
};

export default ImagePrivacyCard;